import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import {
  apiVersionLabel,
  type ProvenanceDescriptor,
} from "./saLockerProvenance"

/**
 * One line saying which API version a piece of data came from and the exact URL
 * that produced it (LOCKER-355 §8).
 *
 * Always visible rather than behind a disclosure — the ticket flagged that as
 * open and chose always-visible, on the grounds that a line you have to go
 * looking for is one nobody reads while a pharmacy is on the phone. It is
 * rendered small and monospace so it reads as machine detail rather than
 * competing with the data.
 *
 * The URL is selectable and wraps rather than being truncated with an ellipsis:
 * its entire purpose is to be copied into `curl`, and a `…` in the middle of a
 * query string defeats that.
 */
export const SaLockerProvenanceLine: FC<{ descriptor: ProvenanceDescriptor }> = ({
  descriptor,
}) => {
  const { t } = useTranslation()
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{ flexWrap: "wrap", rowGap: 0.5 }}
      data-testid={`provenance-${descriptor.apiVersion}`}
    >
      <Chip
        size="small"
        variant="outlined"
        label={apiVersionLabel(descriptor.apiVersion)}
        sx={{ height: 18, fontSize: "0.65rem" }}
      />
      <Typography variant="caption" color="text.secondary">
        {t(descriptor.labelKey)}
      </Typography>
      {!descriptor.isLive && (
        <Tooltip title={t("saLockerConsole.provenance.snapshotHint")}>
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label={t("saLockerConsole.provenance.stored")}
            sx={{ height: 18, fontSize: "0.65rem" }}
          />
        </Tooltip>
      )}
      {/* The METHOD leads the URL (review F1.2). The refresh endpoint only does
          the right thing under POST — a GET to the same path lists Things — so a
          bare URL here reads as a `curl` that will mislead whoever runs it. */}
      <Box
        component="code"
        sx={{
          fontFamily: "monospace",
          fontSize: "0.68rem",
          color: "text.secondary",
          wordBreak: "break-all",
          userSelect: "all",
          minWidth: 0,
        }}
      >
        {`${descriptor.method} ${descriptor.url}`}
      </Box>
      {descriptor.bodyHint && (
        <Box
          component="code"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.68rem",
            color: "text.disabled",
            wordBreak: "break-all",
            userSelect: "all",
            minWidth: 0,
          }}
        >
          {descriptor.bodyHint}
        </Box>
      )}
    </Stack>
  )
}

export default SaLockerProvenanceLine
